import { HttpClient } from '@angular/common/http';
import { Component, EventEmitter, Output, ViewContainerRef, ViewEncapsulation } from '@angular/core';

import { Deferred } from '../../../classes/deferred';
import { LayerService } from '../../../services/layer.service';
import { BaseComponent } from '../../base.component';

@Component({
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-import-external-map',
    templateUrl: './import-external-map.component.html',
    styleUrls: ['../../sharedStyles/base.scss', './import-external-map.component.scss']
})
export class ImportExternalMapComponent extends BaseComponent {
    @Output() readonly done: EventEmitter<boolean> = new EventEmitter();

    private mapURL: URL;
    private _agolMapUrl: string;
    set agolMapUrl(url: string) {
        this._agolMapUrl = url;
        if (url) {
            try {
                this.mapURL = new URL(this._agolMapUrl);
                this.setMapVariables();
            }
            catch {
                this.mapURL = null;
                this.errorMessage = 'Invalid URL';
            }
        } else {
            this.mapURL = null;
            this.webMapId = null;
            this.mapJsonUrl = null;
            this.errorMessage = '';
            this.layers = [];
        }
    }
    get agolMapUrl() {
        return this._agolMapUrl;
    }

    webMapId: string;
    mapJsonUrl: string;
    errorMessage: string;
    esriMap: any;
    loading = false;

    layers: IMapLayer[] = [];
    get selectedLayers() {
        return this.layers.filter(l => l.selected);
    }

    constructor(
        _vcr: ViewContainerRef,
        private readonly http: HttpClient,
        private readonly layerService: LayerService
    ) {
        super(_vcr, true);
    }

    private setMapVariables() {
        this.errorMessage = '';
        this.setWebMapId();
        this.setMapJsonUrl();
    }

    private setWebMapId() {
        this.webMapId = null;
        if (this.mapURL) {
            for (const param of ['id', 'webmap']) {
                if (this.mapURL.searchParams.has(param)) {
                    this.webMapId = this.mapURL.searchParams.get(param).trim();
                    break;
                }
            }
        }
    }

    private async setMapJsonUrl() {
        this.layers = [];
        if (this.webMapId && this.mapURL) {
            await this.getAGOLMapData();
        } else if (this.mapURL) {
            await this.getMapData();
        }
        this.populateLayerData();
    }

    private async getAGOLMapData() {
        const complete = new Deferred<void>();
        this.mapJsonUrl = `${this.mapURL.protocol}//${this.mapURL.host}/sharing/rest/content/items/${this.webMapId}/data?f=json`;
        this.loading = true;
        try {
            this.http.get<IAGOLMapData>(this.mapJsonUrl).subscribe(
                result => {
                    this.loading = false;
                    try {
                        if (result.operationalLayers) {
                            this.layers = result.operationalLayers.map(ol => ({ name: ol.title, url: ol.url, id: ol.id, selected: true }));
                        } else if (result.featureCollection) {
                            this.layers = result.featureCollection.layers.map(ol => ({ name: ol.title, url: ol.url, id: ol.id, selected: true }));
                        }
                        complete.resolve();
                    }
                    catch {
                        this.errorMessage = 'Could not get map data from URL';
                        complete.resolve();
                    }
                },
                er => {
                    this.errorMessage = er && er.message.toLowerCase().startsWith('http') && this.mapJsonUrl.toLowerCase().startsWith('http:')
                    ? 'Content security issue. Try changing the map address to HTTPS instead of HTTP.'
                    : 'Could not get map data from URL';
                    this.loading = false;
                    complete.resolve();
                }
            );
        }
        catch {
            this.loading = false;
            this.errorMessage = 'Could not get map data from URL';
            complete.resolve();
        }
        await complete.promise;
    }

    private async getMapData() {
        const complete = new Deferred<void>();
        this.addJSONTypeToURL(this.mapURL);
        this.loading = true;
        try {
            this.http.get<IExternalMapData>(this.mapURL.toString()).subscribe(
                result => {
                    this.loading = false;
                    try {
                        this.layers = result.layers.map(l => {
                            const url = new URL(this.mapURL.toString());
                            url.pathname += `/${l.id}`;
                            return { name: l.name, id: l.id, url: url.toString(), selected: true };
                        });
                        complete.resolve();
                    }
                    catch {
                        this.errorMessage = 'Could not get map data from URL';
                        complete.resolve();
                    }
                },
                _er => {
                    this.errorMessage = 'Could not get map data from URL';
                    this.loading = false;
                    complete.resolve();
                }
            );
        }
        catch {
            this.loading = false;
            this.errorMessage = 'Could not get map data from URL';
            complete.resolve();
        }
        await complete.promise;
    }

    private populateLayerData() {
        if (this.layers.length) {
            for (const layer of this.layers) {
                const url = new URL(layer.url);
                this.addJSONTypeToURL(url);
                this.http.get<IMapLayerData>(url.toString()).subscribe(
                    result => {
                        layer.description = result.description;
                        layer.data = result;
                        if (!layer.data.url) {
                            layer.data.url = url.toString();
                        }
                    }
                );
            }
        }
    }

    private addJSONTypeToURL(url: URL) {
        if (!url.searchParams.has('f')) {
            url.searchParams.append('f', 'json');
        } else if (url.searchParams.get('f') !== 'json') {
            url.searchParams.set('f', 'json');
        }
    }

    public async addToMap() {
        this.esriMap = null;
        this.errorMessage = '';

        const layerResults: any[] = this.selectedLayers.filter(l => l.data).map(l => l.data);
        if (layerResults.length !== this.selectedLayers.length) {
            layerResults.push(...(await Promise.all(this.selectedLayers.filter(l => !l.data).map(async layer => {
                return await this.layerService.getExternalLayerSymbolData(layer);
            }))));
        }

        await Promise.all(layerResults.filter(r => r).map(layerResult => {
            layerResult.featureEndpoints = [{ url: layerResult.url }];
            layerResult.name = layerResult.name || layerResult.title;
            return this.layerService.addExternalLayersToMap(layerResult);
        }));

        this.done.emit(true);
    }

    public close() {
        this.done.emit(true);
    }

    toggleAllLayers() {
        const selectAll = this.selectedLayers.length !== this.layers.length;
        this.layers.forEach(l => l.selected = selectAll);
    }
}

interface IMapLayer {
    name: string;
    id: string | number;
    url: string;
    selected: boolean;
    description?: string;
    data?: IMapLayerData;
}

interface IExternalMapData {
    currentVersion: number;
    serviceDescription: string;
    mapName: string;
    description: string;
    copyrightText: string;
    supportsDynamicLayers: boolean;
    layers: ILayer[];
    tables: any[];
    spatialReference: ISpatialReference;
    singleFusedMapCache: boolean;
    initialExtent: IInitialExtent;
    fullExtent: IFullExtent;
    minScale: number;
    maxScale: number;
    units: string;
    supportedImageFormatTypes: string;
    documentInfo: IDocumentInfo;
    capabilities: string;
    supportedQueryFormats: string;
    exportTilesAllowed: boolean;
    referenceScale: number;
    datumTransformations: {
        geoTransforms: IGeoTransform[];
    }[];
    supportsDatumTransformation: boolean;
    maxRecordCount: number;
    maxImageHeight: number;
    maxImageWidth: number;
    supportedExtensions: string;
}

interface IAGOLMapData {
    operationalLayers?: IOperationalLayer[];
    featureCollection?: {
        layers: IOperationalLayer[];
    };
    baseMap: {
        baseMapLayers: IBaseMapLayer[];
        title: string;
    };
    spatialReference: ISpatialReference;
    authoringApp: string;
    authoringAppVersion: string;
    version: string;
}

interface IMapLayerData {
    currentVersion: number;
    id: number;
    name: string;
    type: string;
    description: string;
    geometryType: string;
    sourceSpatialReference: ISpatialReference;
    copyrightText: string;
    parentLayer?: any;
    subLayers: any[];
    minScale: number;
    maxScale: number;
    drawingInfo: IDrawingInfo;
    defaultVisibility: boolean;
    extent: IExtent;
    hasAttachments: boolean;
    htmlPopupType: string;
    displayField: string;
    typeIdField?: any;
    subtypeFieldName?: any;
    subtypeField?: any;
    defaultSubtypeCode?: any;
    fields: IField[];
    geometryField: IGeometryField;
    indexes: IIndex[];
    subtypes: any[];
    relationships: any[];
    canModifyLayer: boolean;
    canScaleSymbols: boolean;
    hasLabels: boolean;
    capabilities: string;
    maxRecordCount: number;
    supportsStatistics: boolean;
    supportsAdvancedQueries: boolean;
    supportedQueryFormats: string;
    isDataVersioned: boolean;
    ownershipBasedAccessControlForFeatures: {
        allowOthersToQuery: boolean;
    };
    useStandardizedQueries: boolean;
    advancedQueryCapabilities: IAdvancedQueryCapabilities;
    supportsDatumTransformation: boolean;
    hasGeometryProperties: boolean;
    geometryProperties: IGeometryProperties;
    dateFieldsTimeReference?: any;
    supportsCoordinatesQuantization: boolean;
    url?: string;
}

interface ILayer {
    id: number;
    name: string;
    parentLayerId: number;
    defaultVisibility: boolean;
    subLayerIds?: any;
    minScale: number;
    maxScale: number;
    type: string;
    geometryType: string;
}

interface ISpatialReference {
    wkid: number;
    latestWkid: number;
}

interface IInitialExtent {
    xmin: number;
    ymin: number;
    xmax: number;
    ymax: number;
    spatialReference: ISpatialReference;
}

interface IFullExtent {
    xmin: number;
    ymin: number;
    xmax: number;
    ymax: number;
    spatialReference: ISpatialReference;
}

interface IDocumentInfo {
    Title: string;
    Author: string;
    Comments: string;
    Subject: string;
    Category: string;
    AntialiasingMode: string;
    TextAntialiasingMode: string;
    Keywords: string;
}

interface IGeoTransform {
    wkid: number;
    latestWkid: number;
    transformForward: boolean;
    name: string;
}

interface IFieldInfo {
    fieldName: string;
    label: string;
    tooltip: string;
    visible: boolean;
    stringFieldOption: string;
    format: {
        places: number;
        digitSeparator: boolean;
        dateFormat: string;
    };
}

interface IPopupInfo {
    title: string;
    fieldInfos: IFieldInfo[];
    description?: any;
    showAttachments: boolean;
    mediaInfos: any[];
}

interface IOperationalLayer {
    id: string;
    layerType: string;
    url: string;
    visibility: boolean;
    opacity: number;
    mode: number;
    title: string;
    popupInfo: IPopupInfo;
}

interface IBaseMapLayer {
    id: string;
    layerType: string;
    url: string;
    visibility: boolean;
    opacity: number;
    title: string;
}

interface IOutline {
    type: string;
    style: string;
    color: number[];
    width: number;
}

interface ISymbol {
    type: string;
    style: string;
    color: number[];
    outline: IOutline;
}

interface IRenderer {
    type: string;
    symbol: ISymbol;
    label: string;
    description: string;
}

interface IDrawingInfo {
    renderer: IRenderer;
    transparency: number;
    labelingInfo?: any;
}

interface IExtent {
    xmin: number;
    ymin: number;
    xmax: number;
    ymax: number;
    spatialReference: ISpatialReference;
}

interface IField {
    name: string;
    type: string;
    alias: string;
    domain?: any;
    length?: number;
}

interface IGeometryField {
    name: string;
    type: string;
    alias: string;
}

interface IIndex {
    name: string;
    fields: string;
    isAscending: boolean;
    isUnique: boolean;
    description: string;
}

interface IAdvancedQueryCapabilities {
    useStandardizedQueries: boolean;
    supportsStatistics: boolean;
    supportsHavingClause: boolean;
    supportsCountDistinct: boolean;
    supportsOrderBy: boolean;
    supportsDistinct: boolean;
    supportsPagination: boolean;
    supportsTrueCurve: boolean;
    supportsReturningQueryExtent: boolean;
    supportsQueryWithDistance: boolean;
    supportsSqlExpression: boolean;
}

interface IGeometryProperties {
    shapeAreaFieldName: string;
    shapeLengthFieldName: string;
    units: string;
}
